import {
  Button,
  Dialog,
  Field,
  Input,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { isHttpError, useHttp } from '@mono/http-hook';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

type CreateOrganizationForm = {
  name: string;
};

type Organization = {
  id: string;
  name: string;
  slug: string;
};

type CreateOrganizationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (organization: Organization) => Promise<void> | void;
};

const CreateOrganizationModal: FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const http = useHttp();
  const [serverError, setServerError] = useState<string | null>(null);
  const { handleSubmit, register, formState, reset } =
    useForm<CreateOrganizationForm>({
      defaultValues: {
        name: '',
      },
    });

  useEffect(() => {
    if (!isOpen) {
      reset();
      setServerError(null);
    }
  }, [isOpen, reset]);

  const onSubmit: SubmitHandler<CreateOrganizationForm> = async ({ name }) => {
    setServerError(null);

    try {
      const { data } = await http.post<{ organization: Organization }>(
        '/api/v1/organizations',
        { name: name.trim() },
        { withCredentials: true }
      );

      if (!data?.organization) {
        throw new Error('Unable to create organization. Please try again.');
      }

      toaster.create({
        type: 'success',
        title: 'Organization created',
        description: `"${data.organization.name}" is ready to use.`,
      });

      try {
        await onSuccess?.(data.organization);
      } catch (callbackError) {
        console.error('create-organization:onSuccess', callbackError);
      }
      onClose();
    } catch (error) {
      let message = 'Unable to create organization. Please try again.';

      if (isHttpError(error)) {
        const responseData = error.response?.data;
        if (responseData && typeof responseData === 'object') {
          const { error: errorMessage, message: responseMessage } =
            responseData as {
              error?: string;
              message?: string;
            };
          message = errorMessage ?? responseMessage ?? error.message ?? message;
        } else {
          message = error.message ?? message;
        }
      } else if (error instanceof Error) {
        message = error.message;
      }

      setServerError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to create organization',
        description: message,
      });
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.CloseTrigger />
            <Dialog.Header>
              <Dialog.Title>Create organization</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <form
                id="create-organization-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <Stack gap="4">
                  <Field.Root invalid={Boolean(formState.errors.name)}>
                    <Field.Label>Organization name</Field.Label>
                    <Input
                      autoFocus
                      placeholder="Acme Inc."
                      {...register('name', {
                        required: 'Organization name is required.',
                        minLength: {
                          value: 2,
                          message: 'Name must contain at least 2 characters.',
                        },
                      })}
                    />
                    <Field.ErrorText>
                      {formState.errors.name?.message}
                    </Field.ErrorText>
                  </Field.Root>
                  {serverError ? (
                    <Text color="fg.error" fontSize="sm">
                      {serverError}
                    </Text>
                  ) : null}
                </Stack>
              </form>
            </Dialog.Body>
            <Dialog.Footer gap="3" display="flex" justifyContent="flex-end">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="create-organization-form"
                loading={formState.isSubmitting}
                colorPalette="primary"
              >
                Create
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CreateOrganizationModal;
